import { useEffect, useState } from "react"

import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { Plus, Save, Trash2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { SettingsService } from "@/services/settings-service"

import { createSourceFormSchema, type SourceFormData } from "./trusted-sources-dialog-utils"
import { useDeleteTrustedSource } from "../hooks/use-delete-trusted-source"
import { useGetTrustedSources } from "../hooks/use-get-trusted-sources"
import { useUpsertTrustedSource } from "../hooks/use-upsert-trusted-source"

interface TrustedSourcesDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function CreateNewSourceItem() {
  const { t } = useTranslation()
  return (
    <SelectItem className="text-primary font-medium" value="create-new">
      <div className="flex items-center gap-2">
        <Plus className="h-4 w-4" />
        {t("sources.create_new_source")}
      </div>
    </SelectItem>
  )
}

export function TrustedSourcesDialog({ open, onOpenChange }: TrustedSourcesDialogProps) {
  const { t } = useTranslation()
  const settingsService = SettingsService.getInstance()
  const userPolicy = settingsService.settings.user_policy

  const [selectedSourceId, setSelectedSourceId] = useState<string>("")
  const [isCreatingNew, setIsCreatingNew] = useState(false)
  const [isCompanySource, setIsCompanySource] = useState(false)

  const { data: sourcesData, isPending } = useGetTrustedSources()
  const upsertSourceMutation = useUpsertTrustedSource()
  const deleteSourceMutation = useDeleteTrustedSource()

  const sources = sourcesData?.sources || []
  const sourceFormSchema = createSourceFormSchema(t)

  const form = useForm<SourceFormData>({
    resolver: zodResolver(sourceFormSchema),
    // Validate while typing so the URL error is shown inline; the submit button
    // is disabled on invalid input, so an onSubmit-only mode would never
    // surface why.
    mode: "onChange",
    defaultValues: {
      name: "",
      url: "",
    },
  })

  useEffect(() => {
    if (open) {
      setSelectedSourceId("create-new")
      setIsCompanySource(false)
      form.reset({
        name: "",
        url: "",
      })
      setIsCreatingNew(true)
    }
  }, [open, form])

  const handleSourceSelection = (value: string) => {
    setSelectedSourceId(value)

    if (value === "create-new") {
      setIsCreatingNew(true)
      setIsCompanySource(false)
      form.reset({
        name: "",
        url: "",
      })
    } else {
      setIsCreatingNew(false)
      const selectedSource = sources.find((s) => s.id === value)
      if (selectedSource) {
        setIsCompanySource(selectedSource.type === "company")
        form.reset({
          name: selectedSource.name,
          url: selectedSource.url,
        })
      }
    }
  }

  const onSubmit = async (data: SourceFormData) => {
    try {
      const sourceData = {
        id: isCreatingNew ? undefined : selectedSourceId,
        name: data.name,
        url: data.url,
        type: (isCompanySource ? "company" : "personal") as "company" | "personal",
      }

      await upsertSourceMutation.mutateAsync(sourceData, {
        onSuccess: (data) => {
          toast.success(
            isCreatingNew
              ? t("sources.messages.created_successfully")
              : t("sources.messages.updated_successfully")
          )
          if (isCreatingNew) {
            setSelectedSourceId(data.id)
            setIsCreatingNew(false)
          }
        },
      })
    } catch {
      toast.error(t("sources.messages.failed_to_save"))
    }
  }

  const handleDelete = async () => {
    if (isCreatingNew) return

    if (confirm(t("sources.messages.confirm_delete"))) {
      await deleteSourceMutation.mutateAsync(
        {
          sourceId: selectedSourceId,
          type: isCompanySource ? "company" : "personal",
        },
        {
          onSuccess: () => {
            setSelectedSourceId("")
            form.reset({
              name: "",
              url: "",
            })
            toast.success(t("sources.messages.deleted_successfully"))
          },
        }
      )
    }
  }

  const personalSources = sources.filter((s) => s.type === "personal")
  const companySources = sources.filter((s) => s.type === "company")

  const isAnyMutationPending = upsertSourceMutation.isPending || deleteSourceMutation.isPending
  const isFormDisabled = isCreatingNew ? isAnyMutationPending : isAnyMutationPending || isPending
  const { formState } = form
  const canSubmit = formState.isValid && !isAnyMutationPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>{t("sources.trusted_sources")}</DialogTitle>
          <DialogDescription>{t("sources.trusted_sources_description")}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="source-select">{t("sources.select_source")}</Label>
              <Select
                disabled={isAnyMutationPending}
                value={selectedSourceId}
                onValueChange={handleSourceSelection}
              >
                <SelectTrigger data-testid="trusted-source-select">
                  <SelectValue
                    placeholder={
                      isPending
                        ? t("sources.loading_sources")
                        : t("sources.choose_source_or_create_new")
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>{t("sources.personal_sources")}</SelectLabel>
                    {personalSources.map((source) => (
                      <SelectItem key={source.id} value={source.id}>
                        {source.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                  {userPolicy.is_source_admin && (
                    <SelectGroup>
                      <SelectLabel>{t("sources.company_sources")}</SelectLabel>
                      {companySources.map((source) => (
                        <SelectItem key={source.id} value={source.id}>
                          {source.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                  <CreateNewSourceItem />
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("sources.form.name")}</FormLabel>
                    <FormControl>
                      <Input
                        data-testid="trusted-source-name-input"
                        disabled={isFormDisabled}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("sources.form.url")}</FormLabel>
                    <FormControl>
                      <Input
                        data-testid="trusted-source-url-input"
                        disabled={isFormDisabled}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {userPolicy.is_source_admin && (
              <div className="flex items-center space-x-2">
                <Switch
                  checked={isCompanySource}
                  disabled={isFormDisabled}
                  id="company-source"
                  onCheckedChange={setIsCompanySource}
                />
                <Label htmlFor="company-source">{t("sources.company_source")}</Label>
              </div>
            )}

            <div className="flex justify-end space-x-2">
              {!isCreatingNew && (
                <Button
                  data-testid="trusted-source-delete-button"
                  disabled={isFormDisabled}
                  type="button"
                  variant="destructive"
                  onClick={handleDelete}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t("sources.delete")}
                </Button>
              )}
              <Button
                data-testid="trusted-source-save-button"
                disabled={!canSubmit}
                type="button"
                onClick={form.handleSubmit(onSubmit)}
              >
                <Save className="mr-2 h-4 w-4" />
                {isCreatingNew ? t("sources.create") : t("sources.update")}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
